import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, InputGroup, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';

const HasilRad = () => {
  const navigate = useNavigate();

  // ── State Jam & Tanggal Realtime ──
  const [time, setTime] = useState({ date: '', clock: '' });

  // ── State Pencarian Nota ──
  const [noNotaInput, setNoNotaInput] = useState('');
  const [isLoadingPasien, setIsLoadingPasien] = useState(false);
  
  // ── State Data ──
  const [pasienData, setPasienData] = useState(null);
  const [hasilText, setHasilText] = useState('');
  const [kesanText, setKesanText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // ── State Error Modal ──
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // ── State Preview Cetak ──
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // ── State Cetak Gabungan ──
  const [showKuitansiModal, setShowKuitansiModal] = useState(false);
  const [noKuitansiInput, setNoKuitansiInput] = useState('');

  // Clock Effect
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID') + ' WIB',
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSearchNota = async (e) => {
    if (e) e.preventDefault();
    if (!noNotaInput.trim()) return;

    setErrorMessage('');
    setPasienData(null);
    setHasilText('');
    setKesanText('');
    setIsLoadingPasien(true);

    const apiUrl = getApiUrl();
    const nota = noNotaInput.trim();

    try {
      // Fetch Data Radiologi berdasarkan Nota
      let resPasien;
      try {
        resPasien = await fetch(`${apiUrl}/cekhasilradiologi`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nonota: nota })
        });
      } catch (err) {
        // Fallback GET
        resPasien = await fetch(`${apiUrl}/cekhasilradiologi?nonota=${nota}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (resPasien.ok) {
        const jsonPasien = await resPasien.json();
        if (jsonPasien && (jsonPasien.nmpasien || jsonPasien.status === 1 || jsonPasien.msg === "Nomor bukti transaksi ada !")) {
          setPasienData(jsonPasien);
          setHasilText(jsonPasien.hasil || '');
          setKesanText(jsonPasien.kesan || '');
        } else {
          setErrorMessage(jsonPasien?.msg || 'Data nota radiologi tidak ditemukan.');
          setIsLoadingPasien(false);
          return;
        }
      } else {
        throw new Error(`Gagal memuat data pasien (HTTP ${resPasien.status})`);
      }
    } catch (err) {
      console.error(err);
      const errMsg = err.message || 'Terjadi kesalahan saat menghubungi server.';
      setErrorMessage(errMsg);
      setErrorModalMessage(errMsg);
      setShowErrorModal(true);
    } finally {
      setIsLoadingPasien(false);
    }
  };

  const handleSimpanHasil = () => {
    alert('Fungsi simpan belum tersambung ke endpoint backend. (Menunggu konfirmasi endpoint)');
    console.log('Payload Radiologi:', {
      nonota: noNotaInput,
      hasil: hasilText,
      kesan: kesanText
    });
  };

  const handleRefresh = () => {
    setNoNotaInput('');
    setPasienData(null);
    setHasilText('');
    setKesanText('');
    setErrorMessage('');
  };

  const handleCetakHasil = async () => {
    if (!noNotaInput.trim()) return;
    
    setIsGeneratingPdf(true);
    setErrorMessage('');

    try {
      const res = await fetch(`${getApiUrl()}/cetakhasilrad`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf'
        },
        body: JSON.stringify({ nonota: noNotaInput.trim() })
      });

      if (!res.ok) {
        throw new Error(`Gagal memuat dokumen dari server (HTTP ${res.status})`);
      }

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      setPreviewUrl(objectUrl);
      setShowPreviewModal(true);
    } catch (err) {
      console.error(err);
      setErrorModalMessage(err.message || 'Terjadi kesalahan saat memproses PDF Radiologi.');
      setShowErrorModal(true);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleCetakGabungan = async (e) => {
    if (e) e.preventDefault();
    if (!noKuitansiInput.trim()) return;
    
    setIsGeneratingPdf(true);
    setErrorMessage('');

    try {
      const res = await fetch(`${getApiUrl()}/cetakhasilrad_gab`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf'
        },
        body: JSON.stringify({ nokuitansi: noKuitansiInput.trim() })
      });

      if (!res.ok) {
        throw new Error(`Gagal memuat dokumen dari server (HTTP ${res.status})`);
      }

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      setPreviewUrl(objectUrl);
      setShowKuitansiModal(false);
      setShowPreviewModal(true);
    } catch (err) {
      console.error(err);
      setErrorModalMessage(err.message || 'Terjadi kesalahan saat memproses PDF Gabungan.');
      setShowErrorModal(true);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div style={styles.appContainer}>
      {/* ── NAVBAR HEADER ── */}
      <div style={styles.navbarCustom} className="shadow-sm d-flex justify-content-between align-items-center d-print-none">
        <div className="d-flex align-items-center gap-3">
          <Button variant="link" onClick={() => navigate(-1)} className="p-0 text-white" style={{ textDecoration: 'none' }}>
            <i className="bi bi-arrow-left fs-5"></i>
          </Button>
          <div>
            <h5 className="mb-0 text-white fw-bold d-flex align-items-center gap-2" style={{ letterSpacing: '0.5px' }}>
              <i className="bi bi-file-earmark-medical"></i>
              Input Hasil Radiologi
            </h5>
            <small className="text-white-50" style={{ fontSize: '0.75rem' }}>SIMRS V2 - Modul Radiologi</small>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="text-end d-none d-md-block text-white-50" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
            <div className="text-white fw-bold">{time.clock}</div>
            <div>{time.date}</div>
          </div>
          <Button
            variant="warning"
            size="sm"
            className="fw-semibold text-dark shadow-sm"
            style={{ padding: '0.3rem 0.8rem', fontSize: '0.75rem', borderRadius: '6px' }}
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-house-door-fill me-1"></i> Kembali ke Home
          </Button>
        </div>
      </div>

      <div style={styles.mainContent}>
        <Row className="g-3">
          {/* ── PANEL KIRI: PENCARIAN & INFO PASIEN ── */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff', minHeight: '100%' }}>
              <Card.Header className="bg-white border-bottom-0 pt-3 pb-2 px-4 d-flex justify-content-between align-items-center d-print-none">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                  <i className="bi bi-search text-teal" style={{ color: '#0d9488' }}></i> Cari Nota Radiologi
                </h6>
                <Button 
                  variant="light" 
                  size="sm" 
                  onClick={handleRefresh} 
                  title="Bersihkan form dan mulai dari awal" 
                  className="fw-bold d-flex align-items-center gap-2 shadow-sm" 
                  style={{ 
                    fontSize: '0.75rem', 
                    borderRadius: '20px', 
                    color: '#0f766e', 
                    backgroundColor: '#f0fdfa', 
                    border: '1px solid #ccfbf1', 
                    padding: '0.35rem 0.85rem' 
                  }}
                >
                  <i className="bi bi-arrow-clockwise" style={{ fontSize: '0.85rem' }}></i> Segarkan
                </Button>
              </Card.Header>
              <Card.Body className="px-4">
                <div className="d-print-none">
                  <Form onSubmit={handleSearchNota}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted fw-semibold" style={{ fontSize: '0.75rem' }}>Nomor Nota</Form.Label>
                      <InputGroup>
                        <Form.Control
                          placeholder="Ketik No Nota..."
                          value={noNotaInput}
                          onChange={(e) => setNoNotaInput(e.target.value)}
                          style={{ fontSize: '0.85rem' }}
                        />
                        <Button type="submit" variant="teal" disabled={isLoadingPasien} style={{ background: '#0d9488', borderColor: '#0d9488', color: 'white' }}>
                          {isLoadingPasien ? <Spinner size="sm" animation="border" /> : <i className="bi bi-search"></i>}
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </Form>

                  {errorMessage && (
                    <div className="alert alert-danger py-2 px-3" style={{ fontSize: '0.75rem' }}>
                      <i className="bi bi-exclamation-circle-fill me-2"></i>{errorMessage}
                    </div>
                  )}
                </div>

                {pasienData && (
                  <div className="mt-4 pt-4 border-top">
                    <h6 className="fw-bold text-dark mb-3" style={{ fontSize: '0.85rem' }}>Informasi Pasien</h6>
                    <Row className="g-3" style={{ fontSize: '0.8rem' }}>
                      <Col xs={6}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Nama Pasien</div>
                        <div className="fw-bold text-dark">{pasienData.nmpasien}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>No. RM / Kelas</div>
                        <div className="fw-semibold text-dark">{pasienData.kelas}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Umur / Kelamin</div>
                        <div className="fw-semibold text-dark">{pasienData.umur} • {pasienData.kelamin}</div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Jaminan</div>
                        <div className="fw-semibold text-dark">{pasienData.nmklasifikasi}</div>
                      </Col>
                      <Col xs={12} className="mt-3 pt-3 border-top">
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Dokter Pengirim</div>
                        <div className="fw-bold text-teal" style={{ color: '#0d9488' }}>{pasienData.nmdokter}</div>
                        <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>Tgl: {pasienData.tanggal}</div>
                      </Col>
                      <Col xs={12} className="mt-3 pt-3 border-top">
                        <div className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Pemeriksaan / Tindakan</div>
                        <div className="fw-bold text-dark">{pasienData.nmtindakan}</div>
                      </Col>
                    </Row>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* ── PANEL KANAN: TEXTAREA HASIL RAD ── */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff', minHeight: '100%' }}>
              <Card.Header className="bg-white border-bottom pb-2 pt-3 px-3 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                  <i className="bi bi-card-text text-teal" style={{ color: '#0d9488' }}></i> Uraian Hasil Pemeriksaan
                </h6>
                <div className="d-flex gap-2 d-print-none">
                  <Button variant="outline-primary" size="sm" className="fw-bold px-3 d-flex align-items-center gap-1 shadow-sm" style={{ fontSize: '0.75rem', borderRadius: '6px' }} onClick={() => setShowKuitansiModal(true)}>
                    <i className="bi bi-collection"></i> Cetak Gabungan
                  </Button>
                  {pasienData && (
                    <>
                      <Button variant="outline-secondary" size="sm" className="fw-bold px-3 d-flex align-items-center gap-1 shadow-sm" style={{ fontSize: '0.75rem', borderRadius: '6px' }} onClick={handleCetakHasil} disabled={isGeneratingPdf}>
                        {isGeneratingPdf ? <><Spinner size="sm" animation="border" className="me-2"/> Memproses...</> : <><i className="bi bi-printer-fill"></i> Cetak Nota</>}
                      </Button>
                      <Button variant="success" size="sm" className="fw-bold px-3 d-flex align-items-center gap-1 shadow-sm" style={{ fontSize: '0.75rem', borderRadius: '6px' }} onClick={handleSimpanHasil}>
                        <i className="bi bi-save-fill"></i> Simpan
                      </Button>
                    </>
                  )}
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {!pasienData && !isLoadingPasien ? (
                  <div className="text-center text-muted py-5 mt-4">
                    <i className="bi bi-inbox fs-1 text-secondary mb-3 d-block"></i>
                    Silakan cari Nomor Nota terlebih dahulu untuk melihat form input radiologi.
                  </div>
                ) : (
                  <Form>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>Hasil Pemeriksaan:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={10}
                        value={hasilText}
                        onChange={(e) => setHasilText(e.target.value)}
                        placeholder="Ketikkan uraian hasil pemeriksaan radiologi di sini..."
                        style={{ fontSize: '0.85rem', lineHeight: '1.6', borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
                        className="shadow-none"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>Kesan:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={kesanText}
                        onChange={(e) => setKesanText(e.target.value)}
                        placeholder="Ketikkan kesan atau kesimpulan radiologi..."
                        style={{ fontSize: '0.85rem', lineHeight: '1.6', borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
                        className="shadow-none"
                      />
                    </Form.Group>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* ── Modal Error Notifikasi ── */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered backdrop="static" size="sm">
        <Modal.Body className="text-center p-4">
          <div className="mb-3">
            <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '3rem' }}></i>
          </div>
          <h6 className="fw-bold text-dark mb-2">Peringatan</h6>
          <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
            {errorModalMessage}
          </p>
          <Button variant="danger" size="sm" className="px-4 fw-bold rounded-pill" onClick={() => setShowErrorModal(false)}>
            Mengerti
          </Button>
        </Modal.Body>
      </Modal>

      {/* ── Modal Preview Cetak ── */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" centered backdrop="static">
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '1.1rem' }}>
            <i className="bi bi-printer text-teal" style={{ color: '#0d9488' }}></i>
            Preview Cetak Hasil Radiologi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: '75vh', backgroundColor: '#e2e8f0' }}>
          <iframe 
            id="previewIframeRad"
            src={previewUrl} 
            width="100%" 
            height="100%" 
            style={{ border: 'none' }}
            title="Preview Cetak"
          />
        </Modal.Body>
        <Modal.Footer className="bg-light border-top">
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Tutup
          </Button>
          <a href={previewUrl} download={`HasilRad_${noKuitansiInput || noNotaInput}.pdf`} className="btn btn-outline-primary d-flex align-items-center gap-1">
            <i className="bi bi-download"></i> Download PDF
          </a>
          <Button 
            variant="teal" 
            style={{ background: '#0d9488', borderColor: '#0d9488', color: 'white' }} 
            onClick={() => {
              const iframe = document.getElementById('previewIframeRad');
              if(iframe) {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
              }
            }}
          >
            <i className="bi bi-printer-fill me-1"></i> Cetak Dokumen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal Input Kuitansi untuk Cetak Gabungan ── */}
      <Modal show={showKuitansiModal} onHide={() => setShowKuitansiModal(false)} centered backdrop="static" size="sm">
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '1rem' }}>
            <i className="bi bi-input-cursor-text text-primary"></i> Input Kuitansi
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleCetakGabungan}>
            <Form.Group className="mb-3">
              <Form.Label className="text-muted fw-semibold" style={{ fontSize: '0.75rem' }}>Nomor Kuitansi</Form.Label>
              <Form.Control
                autoFocus
                placeholder="Masukkan Nomor Kuitansi..."
                value={noKuitansiInput}
                onChange={(e) => setNoKuitansiInput(e.target.value)}
                style={{ fontSize: '0.85rem' }}
              />
            </Form.Group>
            <Button type="submit" variant="primary" size="sm" className="w-100 fw-bold rounded-pill" disabled={!noKuitansiInput.trim() || isGeneratingPdf}>
              {isGeneratingPdf ? <><Spinner size="sm" animation="border" className="me-2"/> Memproses...</> : <><i className="bi bi-search me-1"></i> Preview Cetak Gabungan</>}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

    </div>
  );
};

const styles = {
  appContainer: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.45rem 1rem',
    borderBottom: '2px solid #0d9488'
  },
  mainContent: {
    padding: '0.65rem 0.85rem'
  }
};

export default HasilRad;
